import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import type { Phase4PaymentConfig } from "@/integrations/multiresume/payment";

import { LegalPage } from "@/components/legal/legal-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuthSession } from "@/hooks/use-auth-session";
import { getDefaultPhase4PaymentConfig, PUBLIC_STARTER_PACK } from "@/integrations/multiresume/payment";
import { orpc } from "@/integrations/orpc/client";
import { multiResumeBrand } from "@/utils/multiresume-brand";

const STARTER_PACK_DISPLAY_NAME = "Starter Pack";

type PricingPageCopy = {
  title: string;
  description: string;
  oneTimePackage: string;
  inviteOnlyBeta: string;
  packageUnavailable: string;
  unavailableBody: string;
  price: string;
  includes: string;
  aiUnits: string;
  validity: string;
  unavailable: string;
  noExpiryConfigured: string;
  days: (days: number) => string;
  noSubscription: string;
  taxesBeforeConfirmation: string;
  purchasesNotOpen: string;
  purchaseAccess: string;
  quotaTitle: string;
  quotaBody: string;
  regularEditing: string;
  paymentTitle: string;
  paymentBody: string;
  eligibilityBody: string;
  boundariesTitle: string;
  boundariesBody: string;
  refundsTitle: string;
  acceptableUse: string;
  refundPolicy: string;
  terms: string;
  privacy: string;
  support: string;
  noticesTitle: string;
  purchaseAccessTitle: string;
  signInToCheckAccess: string;
  signIn: string;
  checkAccessUnavailable: string;
  eligibleAccess: string;
  continueToBilling: string;
  requestPurchaseAccess: string;
  disabledAccess: string;
  pausedAccess: string;
  notWhitelistedAccess: string;
  whitelistExpiredAccess: string;
  packageUnavailableAccess: string;
  packageNotAllowedAccess: string;
  capReachedAccess: string;
  pendingOrderAccess: string;
};

export const pricingMessages = {
  title: msg`Pricing`,
  description: msg`MultiResume is currently in an invite-only beta. Purchase access is limited to eligible beta accounts.`,
  oneTimePackage: msg`One-time AI quota package`,
  inviteOnlyBeta: msg`Invite-only beta`,
  packageUnavailable: msg`Package unavailable`,
  unavailableBody: msg`Purchase access is currently unavailable until the package price, quota, and validity are configured. Eligible beta accounts can still use the regular resume editor features that do not require paid AI quota.`,
  price: msg`Price`,
  includes: msg`Includes`,
  aiUnits: msg`AI units`,
  validity: msg`Validity`,
  unavailable: msg`Unavailable`,
  noExpiryConfigured: msg`No expiry configured`,
  days: msg`{days} days`,
  noSubscription: msg`No subscription. No automatic renewal.`,
  taxesBeforeConfirmation: msg`Applicable taxes, if any, are calculated and shown before checkout confirmation.`,
  purchasesNotOpen: msg`Purchases are not yet open. Package details remain visible for account review and planning.`,
  purchaseAccess: msg`Purchase access is limited to invited, eligible beta accounts. Checkout availability is controlled inside the signed-in app and does not open from this public page.`,
  quotaTitle: msg`What AI quota covers`,
  quotaBody: msg`AI quota can be used for resume/PDF parsing, JD parsing/OCR, AI-assisted resume review suggestions, target resume optimization suggestions, and section/item AI optimization.`,
  regularEditing: msg`Regular editing, saving, preview, and export do not consume AI quota.`,
  paymentTitle: msg`Payment and eligibility`,
  paymentBody: msg`Secure checkout is provided by Waffo Pancake for eligible beta purchases. Waffo Pancake checkout handles card and payment method details, while MultiResume stores only the order, payment, and refund metadata needed for confirmation, quota grants, support, and audit.`,
  eligibilityBody: msg`Purchase eligibility is limited by invite status, account whitelist, beta review, and operational controls. Public pricing information does not bypass those checkout controls, and checkout return pages alone do not confirm payment truth.`,
  boundariesTitle: msg`Service boundaries`,
  boundariesBody: msg`MultiResume does not provide recruitment, job placement, candidate matching, application submission, or guaranteed outcomes. AI suggestions are review tools and should be checked by the user before use.`,
  refundsTitle: msg`Refunds and support`,
  acceptableUse: msg`Acceptable Use Policy`,
  refundPolicy: msg`Refund Policy`,
  terms: msg`Terms of Service`,
  privacy: msg`Privacy Policy`,
  support: msg`Support`,
  noticesTitle: msg`Additional service notices`,
  purchaseAccessTitle: msg`Purchase access for your account`,
  signInToCheckAccess: msg`Sign in to check whether your beta account can start a purchase.`,
  signIn: msg`Sign in`,
  checkAccessUnavailable: msg`Purchase access could not be checked. Try again from billing or contact support.`,
  eligibleAccess: msg`Your account is eligible to continue to secure checkout.`,
  continueToBilling: msg`Continue to billing`,
  requestPurchaseAccess: msg`Request purchase access`,
  disabledAccess: msg`Your account is unavailable for purchases. Contact support.`,
  pausedAccess: msg`Purchases are temporarily paused. Contact support.`,
  notWhitelistedAccess: msg`This beta account does not yet have purchase access. Request access from support.`,
  whitelistExpiredAccess: msg`This beta purchase access has expired. Request access from support.`,
  packageUnavailableAccess: msg`No purchase package is currently available for this account.`,
  packageNotAllowedAccess: msg`This account cannot purchase the currently available package.`,
  capReachedAccess: msg`This account has reached its purchase limit.`,
  pendingOrderAccess: msg`A checkout is already pending. Continue it from billing instead of paying again.`,
};

type PublicPricingPackage = {
  available: boolean;
  displayName: string;
  priceDisplay: string | null;
  quotaUnits: number | null;
  validityDays: number | null;
  supportEmail: string;
};

const getPricingServerFn = createServerFn({ method: "GET" }).handler(async () => {
  return getPublicPricingPackage(
    getDefaultPhase4PaymentConfig(),
    process.env.MULTIRESUME_SUPPORT_EMAIL?.trim() || multiResumeBrand.supportEmail,
  );
});

export const Route = createFileRoute("/pricing")({
  component: RouteComponent,
  loader: async () => getPricingServerFn(),
  head: () => ({
    meta: [
      { title: `Pricing - ${multiResumeBrand.appName}` },
      {
        name: "description",
        content:
          "MultiResume invite-only beta pricing for one-time AI quota packages with public trust and payment notices.",
      },
    ],
  }),
});

export function getPublicPricingPackage(
  _config: Phase4PaymentConfig,
  supportEmail: string = multiResumeBrand.supportEmail,
): PublicPricingPackage {
  return {
    available: true,
    displayName: STARTER_PACK_DISPLAY_NAME,
    priceDisplay: PUBLIC_STARTER_PACK.priceDisplay,
    quotaUnits: PUBLIC_STARTER_PACK.quotaUnits,
    validityDays: PUBLIC_STARTER_PACK.validityDays,
    supportEmail,
  };
}

function formatValidity(packageInfo: PublicPricingPackage, copy: PricingPageCopy) {
  if (!packageInfo.available) return copy.unavailable;
  if (packageInfo.validityDays === 0 || packageInfo.validityDays === null) return copy.noExpiryConfigured;
  return copy.days(packageInfo.validityDays);
}

function PackageCard({ packageInfo }: { packageInfo: PublicPricingPackage }) {
  const copy = getPricingCopy();

  if (!packageInfo.available) {
    return (
      <section className="rounded-md border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2>{packageInfo.displayName}</h2>
            <p>{copy.oneTimePackage}</p>
          </div>
          <Badge variant="secondary">{copy.packageUnavailable}</Badge>
        </div>

        <p className="mt-4">{copy.unavailableBody}</p>
      </section>
    );
  }

  return (
    <section className="rounded-md border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2>{packageInfo.displayName}</h2>
          <p>{copy.oneTimePackage}</p>
        </div>
        <Badge variant="secondary">{copy.inviteOnlyBeta}</Badge>
      </div>

      <dl className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <dt className="text-xs font-medium text-muted-foreground">{copy.price}</dt>
          <dd className="mt-1 text-lg font-semibold text-foreground">{packageInfo.priceDisplay}</dd>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <dt className="text-xs font-medium text-muted-foreground">{copy.includes}</dt>
          <dd className="mt-1 text-lg font-semibold text-foreground">
            {packageInfo.quotaUnits} {copy.aiUnits}
          </dd>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <dt className="text-xs font-medium text-muted-foreground">{copy.validity}</dt>
          <dd className="mt-1 text-lg font-semibold text-foreground">{formatValidity(packageInfo, copy)}</dd>
        </div>
      </dl>

      <p className="mt-4 font-medium text-foreground">{copy.noSubscription}</p>
      <p className="mt-2 text-sm text-muted-foreground">{copy.taxesBeforeConfirmation}</p>
      <p className="mt-2 font-medium text-foreground">{copy.purchasesNotOpen}</p>
      <p className="mt-2 text-sm text-muted-foreground">{copy.purchaseAccess}</p>
    </section>
  );
}

export function getPricingCopy(currentI18n = i18n): PricingPageCopy {
  return {
    title: currentI18n._(pricingMessages.title),
    description: currentI18n._(pricingMessages.description),
    oneTimePackage: currentI18n._(pricingMessages.oneTimePackage),
    inviteOnlyBeta: currentI18n._(pricingMessages.inviteOnlyBeta),
    packageUnavailable: currentI18n._(pricingMessages.packageUnavailable),
    unavailableBody: currentI18n._(pricingMessages.unavailableBody),
    price: currentI18n._(pricingMessages.price),
    includes: currentI18n._(pricingMessages.includes),
    aiUnits: currentI18n._(pricingMessages.aiUnits),
    validity: currentI18n._(pricingMessages.validity),
    unavailable: currentI18n._(pricingMessages.unavailable),
    noExpiryConfigured: currentI18n._(pricingMessages.noExpiryConfigured),
    days: (days) => currentI18n._({ ...pricingMessages.days, values: { days } }),
    noSubscription: currentI18n._(pricingMessages.noSubscription),
    taxesBeforeConfirmation: currentI18n._(pricingMessages.taxesBeforeConfirmation),
    purchasesNotOpen: currentI18n._(pricingMessages.purchasesNotOpen),
    purchaseAccess: currentI18n._(pricingMessages.purchaseAccess),
    quotaTitle: currentI18n._(pricingMessages.quotaTitle),
    quotaBody: currentI18n._(pricingMessages.quotaBody),
    regularEditing: currentI18n._(pricingMessages.regularEditing),
    paymentTitle: currentI18n._(pricingMessages.paymentTitle),
    paymentBody: currentI18n._(pricingMessages.paymentBody),
    eligibilityBody: currentI18n._(pricingMessages.eligibilityBody),
    boundariesTitle: currentI18n._(pricingMessages.boundariesTitle),
    boundariesBody: currentI18n._(pricingMessages.boundariesBody),
    refundsTitle: currentI18n._(pricingMessages.refundsTitle),
    acceptableUse: currentI18n._(pricingMessages.acceptableUse),
    refundPolicy: currentI18n._(pricingMessages.refundPolicy),
    terms: currentI18n._(pricingMessages.terms),
    privacy: currentI18n._(pricingMessages.privacy),
    support: currentI18n._(pricingMessages.support),
    noticesTitle: currentI18n._(pricingMessages.noticesTitle),
    purchaseAccessTitle: currentI18n._(pricingMessages.purchaseAccessTitle),
    signInToCheckAccess: currentI18n._(pricingMessages.signInToCheckAccess),
    signIn: currentI18n._(pricingMessages.signIn),
    checkAccessUnavailable: currentI18n._(pricingMessages.checkAccessUnavailable),
    eligibleAccess: currentI18n._(pricingMessages.eligibleAccess),
    continueToBilling: currentI18n._(pricingMessages.continueToBilling),
    requestPurchaseAccess: currentI18n._(pricingMessages.requestPurchaseAccess),
    disabledAccess: currentI18n._(pricingMessages.disabledAccess),
    pausedAccess: currentI18n._(pricingMessages.pausedAccess),
    notWhitelistedAccess: currentI18n._(pricingMessages.notWhitelistedAccess),
    whitelistExpiredAccess: currentI18n._(pricingMessages.whitelistExpiredAccess),
    packageUnavailableAccess: currentI18n._(pricingMessages.packageUnavailableAccess),
    packageNotAllowedAccess: currentI18n._(pricingMessages.packageNotAllowedAccess),
    capReachedAccess: currentI18n._(pricingMessages.capReachedAccess),
    pendingOrderAccess: currentI18n._(pricingMessages.pendingOrderAccess),
  };
}

export function PricingPageView({ packageInfo }: { packageInfo: PublicPricingPackage }) {
  const { i18n } = useLingui();
  const copy = getPricingCopy(i18n);

  return (
    <LegalPage title={copy.title} description={copy.description}>
      <PackageCard packageInfo={packageInfo} />
      <PricingPurchaseAccess />

      <section>
        <h2>{copy.quotaTitle}</h2>
        <p>{copy.quotaBody}</p>
        <p>{copy.regularEditing}</p>
      </section>

      <section>
        <h2>{copy.paymentTitle}</h2>
        <p>{copy.paymentBody}</p>
        <p>{copy.eligibilityBody}</p>
      </section>

      <section>
        <h2>{copy.boundariesTitle}</h2>
        <p>{copy.boundariesBody}</p>
      </section>

      <section>
        <h2>{copy.refundsTitle}</h2>
        <p>
          <Trans>
            For payment, quota, refund, privacy, deletion, or export requests, contact support:{" "}
            <a href={`mailto:${packageInfo.supportEmail}`}>{packageInfo.supportEmail}</a>.
          </Trans>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" nativeButton={false} render={<a href="/refund-policy">{copy.refundPolicy}</a>} />
          <Button variant="outline" nativeButton={false} render={<a href="/terms">{copy.terms}</a>} />
          <Button variant="outline" nativeButton={false} render={<a href="/privacy">{copy.privacy}</a>} />
          <Button
            variant="outline"
            nativeButton={false}
            render={<a href="/acceptable-use-policy">{copy.acceptableUse}</a>}
          />
          <Button variant="outline" nativeButton={false} render={<a href="/support">{copy.support}</a>} />
        </div>
      </section>

      <section>
        <h2>{copy.noticesTitle}</h2>
        <p>
          <Trans>
            For more detail about AI suggestions and cross-border infrastructure, see the{" "}
            <a href="/ai-service">AI Service Notice</a> and{" "}
            <a href="/cross-border-processing">Cross-border Data Processing</a> notice.
          </Trans>
        </p>
      </section>
    </LegalPage>
  );
}

function PricingPurchaseAccess() {
  const { i18n } = useLingui();
  const copy = getPricingCopy(i18n);
  const session = useAuthSession();

  if (!session) {
    return (
      <section data-phase6-state="pricing-signed-out">
        <h2>{copy.purchaseAccessTitle}</h2>
        <p>{copy.signInToCheckAccess}</p>
        <a className="font-medium underline underline-offset-4" href="/auth/login">
          {copy.signIn}
        </a>
      </section>
    );
  }

  return <AuthenticatedPricingPurchaseAccess />;
}

function AuthenticatedPricingPurchaseAccess() {
  const { i18n } = useLingui();
  const copy = getPricingCopy(i18n);
  const accountSummary = useQuery(orpc.multiResumeBilling.accountSummary.queryOptions());

  if (accountSummary.isError) {
    return (
      <section data-phase6-state="pricing-access-unavailable">
        <h2>{copy.purchaseAccessTitle}</h2>
        <p>{copy.checkAccessUnavailable}</p>
        <a className="font-medium underline underline-offset-4" href="/dashboard/billing">
          {copy.continueToBilling}
        </a>
      </section>
    );
  }

  if (accountSummary.isPending || !accountSummary.data) return null;
  const eligibility = accountSummary.data.purchaseEligibility;
  const body =
    eligibility.state === "eligible"
      ? copy.eligibleAccess
      : eligibility.state === "disabled"
        ? copy.disabledAccess
        : eligibility.state === "paused"
          ? copy.pausedAccess
          : eligibility.state === "not_whitelisted"
            ? copy.notWhitelistedAccess
            : eligibility.state === "whitelist_expired"
              ? copy.whitelistExpiredAccess
              : eligibility.state === "package_unavailable"
                ? copy.packageUnavailableAccess
                : eligibility.state === "package_not_allowed"
                  ? copy.packageNotAllowedAccess
                  : eligibility.state === "cap_reached"
                    ? copy.capReachedAccess
                    : copy.pendingOrderAccess;
  const mailto = `mailto:${multiResumeBrand.supportEmail}?subject=${encodeURIComponent("Purchase access")}&body=${encodeURIComponent(`Issue class: ${eligibility.state}`)}`;

  return (
    <section data-phase6-state={`pricing-${eligibility.state}`}>
      <h2>{copy.purchaseAccessTitle}</h2>
      <p>{body}</p>
      {eligibility.state === "eligible" || eligibility.state === "pending_order" ? (
        <a className="font-medium underline underline-offset-4" href="/dashboard/billing">
          {copy.continueToBilling}
        </a>
      ) : (
        <a
          className="font-medium underline underline-offset-4"
          data-phase6-entry="purchase-access-support"
          href={mailto}
        >
          {copy.requestPurchaseAccess}
        </a>
      )}
    </section>
  );
}

function RouteComponent() {
  const packageInfo = Route.useLoaderData();
  return <PricingPageView packageInfo={packageInfo} />;
}
